import { QBS_DATA_TEST_ID } from '@e2e/constants/exam-management';
import type {
  CreateQBankStructureFormInput,
  GroupFormFieldSlotProps,
} from '@exam-management/common/types';
import { QBankStructureInformationFormFields } from '@exam-management/components';
import { QBankStructureType } from '@exam-management/graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Form, DialogContent, DialogActions, Divider } from '@shared/components';
import { useForm, FormProvider } from 'react-hook-form';
import { z } from 'zod';

const NAME_MIN_LENGTH = 1;
const NAME_MAX_LENGTH = 1024;
const DESCRIPTION_MAX_LENGTH = 1024;
const REMARK_MAX_LENGTH = 1024;
const GROUP_NAME_MAX_LENGTH = 256;

const CreateQBankStructureFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(NAME_MIN_LENGTH, {
      message: 'กรุณากรอกชื่อคลังข้อสอบ',
    })
    .max(NAME_MAX_LENGTH, {
      message: `ชื่อคลังข้อสอบต้องมีความยาวไม่เกิน ${NAME_MAX_LENGTH} ตัวอักษร`,
    }),
  description: z
    .string()
    .trim()
    .max(DESCRIPTION_MAX_LENGTH, {
      message: `รายละเอียดคลังข้อสอบต้องมีความยาวไม่เกิน ${DESCRIPTION_MAX_LENGTH} ตัวอักษร`,
    }),
  type: z.nativeEnum(QBankStructureType, {
    message: `โปรดเลือกประเภทคลังข้อสอบ`,
  }),
  remark: z
    .string()
    .trim()
    .max(REMARK_MAX_LENGTH, {
      message: `หมายเหตุหลักสูตรต้องมีความยาวไม่เกิน ${REMARK_MAX_LENGTH} ตัวอักษร`,
    }),
  group: z
    .object({
      inputValue: z.string().nullish(),
      label: z.string(),
      value: z.string(),
    })
    .nullish()
    .refine(
      (data) =>
        !data ||
        ((data.inputValue?.length ?? 0) <= GROUP_NAME_MAX_LENGTH &&
          data.value.length <= GROUP_NAME_MAX_LENGTH),
      {
        message: `ชื่อกลุ่มต้องมีความยาวไม่เกิน ${GROUP_NAME_MAX_LENGTH} ตัวอักษร`,
      },
    ),
});

interface CreateQBankStructureFormProps {
  onClose: () => void;
  onCreateSubmit: (formValues: CreateQBankStructureFormInput) => Promise<void>;
  loading?: boolean;
  slotProps: {
    group: GroupFormFieldSlotProps;
  };
}

export default function CreateQBankStructureForm(props: Readonly<CreateQBankStructureFormProps>) {
  const { onClose, onCreateSubmit, loading: isLoading = false, slotProps } = props;
  const formContext = useForm<CreateQBankStructureFormInput>({
    defaultValues: {
      name: '',
      description: '',
      remark: '',
      group: null,
    },
    resolver: zodResolver(CreateQBankStructureFormSchema),
  });

  const { handleSubmit, reset } = formContext;

  const onSubmit = async (data: CreateQBankStructureFormInput) => {
    await onCreateSubmit(data);
    reset();
  };

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <QBankStructureInformationFormFields
            slotProps={slotProps}
            dataTestId={QBS_DATA_TEST_ID.CREATE_FORM}
          />
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button
            variant='outlined'
            onClick={onClose}
            color='secondary'
            disabled={isLoading}
            size='large'
          >
            Cancel
          </Button>
          <Button
            type='submit'
            color='primary'
            loading={isLoading}
            data-testid={QBS_DATA_TEST_ID.CREATE_FORM_SUBMIT_BUTTON}
            size='large'
          >
            Create QBank
          </Button>
        </DialogActions>
      </Form>
    </FormProvider>
  );
}
